<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { ConnectionTool, DeviceAction, RemoteUiAdapter } from '../model.js';
import FilesPanel from './FilesPanel.vue';
import PrivilegePanel from './PrivilegePanel.vue';
import type { DirectoryDevice } from '../lib/directory.js';
import { formatTimestamp, presenceLabel, presenceTone } from '../lib/format.js';
import { describeOperatingSystem } from '../lib/operating-system.js';
import type { DeviceTab } from '../lib/route.js';
import AppIcon from './AppIcon.vue';
import DeviceAddress from './DeviceAddress.vue';
import DeviceGlyph from './DeviceGlyph.vue';
import OsIdentity from './OsIdentity.vue';
import StatusPill from './StatusPill.vue';
import ToolPreview from './ToolPreview.vue';
import ServerPanel from './ServerPanel.vue';
import type { IconName } from './icons.js';
import type { DeviceActionRequest } from '@atum-remote/protocol/device-actions';
import MenuButton from './MenuButton.vue';
import MenuItem from './MenuItem.vue';

const props = defineProps<{
  device: DirectoryDevice;
  tab: DeviceTab;
  opening: boolean;
  busy: boolean;
  preferencesBusy?: boolean;
  adapter?: RemoteUiAdapter;
  /** Whether this person may remove the device; the host confirms before anything happens. */
  removable?: boolean;
}>();
const emit = defineEmits<{
  open: [tool: ConnectionTool];
  customize: [];
  remove: [];
  navigate: [tab: DeviceTab];
  deviceAction: [action: DeviceActionRequest['action']];
}>();

interface Tab {
  readonly id: DeviceTab;
  readonly label: string;
  readonly icon: IconName;
  readonly preview: boolean;
}
const allTabs: readonly Tab[] = [
  { id: 'overview', label: 'Overview', icon: 'devices', preview: false },
  { id: 'files', label: 'Files', icon: 'files', preview: false },
  { id: 'logs', label: 'Logs', icon: 'logs', preview: true },
  {
    id: 'resources',
    label: 'Resources',
    icon: 'resources',
    preview: true,
  },
];
const tabs = computed(() =>
  allTabs
    .filter((tab) => !props.device.personalAccess || ['overview', 'files'].includes(tab.id))
    .map((tab) => ({ ...tab, preview: tab.preview && !isServer.value })),
);
const isServer = computed(() => props.device.hostProfile === 'linux-server');
const journalUnit = ref('');

interface Tool {
  readonly id: ConnectionTool;
  readonly icon: IconName;
  readonly title: string;
  readonly action: string;
  readonly description: string;
}
const tools = computed<readonly Tool[]>(() =>
  (
    [
      {
        id: 'desktop',
        icon: 'devices',
        title: 'Desktop',
        action: 'Open desktop',
        description:
          props.device.desktop.available && props.device.desktop.controlAvailable === false
            ? 'See the screen of this device.'
            : 'See the screen and control this device.',
      },
      {
        id: 'terminal',
        icon: 'terminal',
        title: 'Terminal',
        action: 'Open terminal',
        description: 'Open a shell on this device.',
      },
      {
        id: 'nyx',
        icon: 'bolt',
        title: 'AI work',
        action: 'Open AI work',
        description:
          'Start and follow Codex, Claude Code and Cursor Agent tasks in repositories linked on this device through Nyx Link.',
      },
    ] satisfies Tool[]
  )
    .filter(
      (tool) =>
        (!props.device.personalAccess || tool.id === 'desktop') &&
        (!isServer.value || tool.id !== 'nyx'),
    )
    .sort((a, b) =>
      isServer.value ? Number(b.id === 'terminal') - Number(a.id === 'terminal') : 0,
    ),
);
const unavailable: DeviceAction = {
  available: false,
  explanation: 'Nyx access is not available in this connection.',
};
/** AI work is optional on a device; the other tools are always described. */
const availability = (tool: ConnectionTool): DeviceAction => props.device[tool] ?? unavailable;
const connection = computed(() =>
  props.device.source === 'saved' ? 'Owner SSH connection' : 'Registered with server',
);
const systemFallback = computed(() =>
  props.device.source === 'saved' ? 'System checked when you connect' : undefined,
);
const system = computed(() =>
  describeOperatingSystem(
    props.device.reportedPlatform ?? props.device.platform,
    props.device.osVersion,
  ),
);
watch(
  () => props.device.id,
  () => {
    journalUnit.value = '';
  },
);
</script>

<template>
  <section class="device-page">
    <header class="device-header panel">
      <DeviceGlyph
        :icon="props.device.icon"
        :color="props.device.color"
        :platform="props.device.reportedPlatform ?? props.device.platform"
        :os-version="props.device.osVersion"
        :presence="props.device.presence"
        size="lg"
      />
      <div class="device-header-text">
        <div class="detail-hero-row">
          <StatusPill :tone="presenceTone[props.device.presence]">
            {{ presenceLabel[props.device.presence] }}
          </StatusPill>
          <span class="tag">{{ connection }}</span>
          <span v-if="isServer" class="tag">Headless server</span>
          <span class="tag"><AppIcon name="folder" />{{ props.device.group }}</span>
          <span
            v-if="props.device.name !== props.device.reportedName"
            class="tag"
            :title="`Reported as ${props.device.reportedName}`"
          >
            Reported as {{ props.device.reportedName }}
          </span>
        </div>
        <p class="detail-host">{{ props.device.hostname }}</p>
        <p class="detail-sub">
          <OsIdentity
            :platform="props.device.reportedPlatform ?? props.device.platform"
            :version="props.device.osVersion"
            :fallback="systemFallback"
          />
          <span v-if="props.device.architecture" class="detail-sub-item mono">{{
            props.device.architecture
          }}</span>
          <DeviceAddress v-if="props.device.network" :network="props.device.network" />
        </p>
      </div>
      <div class="device-header-actions">
        <MenuButton
          v-if="
            props.adapter?.runDeviceAction &&
            !props.device.personalAccess &&
            props.device.source === 'managed' &&
            ['windows', 'macos'].includes(props.device.platform)
          "
          label="Device actions"
          icon="power"
          :disabled="props.busy || props.opening || !props.device.terminal.available"
        >
          <MenuItem icon="lock" @click="emit('deviceAction', 'lock')">Lock…</MenuItem>
          <MenuItem icon="refresh" @click="emit('deviceAction', 'restart')">Restart…</MenuItem>
          <MenuItem icon="power" @click="emit('deviceAction', 'shutdown')">Shut down…</MenuItem>
        </MenuButton>
        <button
          type="button"
          class="quiet"
          title="Customize name, group, icon and color"
          :disabled="props.preferencesBusy"
          @click="emit('customize')"
        >
          <AppIcon name="edit" /><span class="action-label">Customize</span>
        </button>
        <button
          v-if="!props.device.personalAccess && !isServer"
          type="button"
          :disabled="!availability('nyx').available || props.opening"
          :title="availability('nyx').explanation"
          @click="emit('open', 'nyx')"
        >
          <AppIcon name="bolt" /><span class="action-label">AI work</span>
        </button>
        <button
          v-if="!props.device.personalAccess"
          type="button"
          :disabled="!props.device.terminal.available || props.opening"
          :title="props.device.terminal.explanation"
          :class="{ primary: isServer }"
          @click="emit('open', 'terminal')"
        >
          <AppIcon name="terminal" /><span class="action-label">Terminal</span>
        </button>
        <button
          v-if="!isServer"
          type="button"
          class="primary"
          :disabled="!props.device.desktop.available || props.opening"
          :title="props.device.desktop.explanation"
          @click="emit('open', 'desktop')"
        >
          <AppIcon name="devices" /><span class="action-label">Desktop</span>
        </button>
      </div>
    </header>

    <nav class="tab-strip" aria-label="Device tools">
      <button
        v-for="item in tabs"
        :key="item.id"
        type="button"
        class="tab"
        :aria-current="props.tab === item.id ? 'page' : undefined"
        @click="emit('navigate', item.id)"
      >
        <AppIcon :name="item.icon" />{{ item.label
        }}<span v-if="item.preview" class="tag soon">Soon</span>
      </button>
    </nav>

    <template v-if="props.tab === 'overview'">
      <div class="connect-grid">
        <article
          v-for="tool in tools"
          :key="tool.id"
          class="connect-card panel"
          :data-available="
            isServer && tool.id === 'desktop'
              ? 'true'
              : availability(tool.id).available
                ? 'true'
                : 'false'
          "
        >
          <div class="connect-card-head">
            <AppIcon :name="tool.icon" />
            <h2>{{ tool.title }}</h2>
          </div>
          <template v-if="isServer && tool.id === 'desktop'">
            <h3>Headless server</h3>
            <p>
              This server runs without a graphical desktop. Manage it through Terminal, Resources
              and Logs.
            </p>
          </template>
          <p v-else>{{ tool.description }}</p>
          <button
            v-if="!isServer || tool.id !== 'desktop'"
            type="button"
            class="primary"
            :disabled="!availability(tool.id).available || props.opening"
            @click="emit('open', tool.id)"
          >
            <AppIcon :name="tool.icon" />{{ props.opening ? 'Opening…' : tool.action }}
          </button>
          <p v-if="!isServer || tool.id !== 'desktop'" class="hint">
            {{ availability(tool.id).explanation }}
          </p>
        </article>
      </div>

      <dl class="facts panel">
        <div>
          <dt>Connection</dt>
          <dd>{{ connection }}</dd>
        </div>
        <div>
          <dt>{{ props.device.source === 'saved' ? 'SSH alias' : 'Hostname' }}</dt>
          <dd class="mono">{{ props.device.hostname }}</dd>
        </div>
        <div>
          <dt>System</dt>
          <dd>
            <OsIdentity
              :platform="props.device.reportedPlatform ?? props.device.platform"
              :version="props.device.osVersion"
              :fallback="systemFallback"
              compact
            />
          </dd>
        </div>
        <div v-if="system?.reported || system?.version">
          <dt>OS version</dt>
          <dd class="mono">{{ system.reported || system.version }}</dd>
        </div>
        <div v-if="props.device.architecture">
          <dt>Architecture</dt>
          <dd class="mono">{{ props.device.architecture }}</dd>
        </div>
        <div v-if="props.device.appVersion">
          <dt>ATUM Remote client</dt>
          <dd class="mono">{{ props.device.appVersion }}</dd>
        </div>
        <div v-if="props.device.source === 'managed'">
          <dt>Last seen</dt>
          <dd class="mono">{{ formatTimestamp(props.device.lastSeenAt) }}</dd>
        </div>
        <div>
          <dt>Group</dt>
          <dd>{{ props.device.group }}</dd>
        </div>
        <div v-if="props.device.name !== props.device.reportedName">
          <dt>Reported name</dt>
          <dd>{{ props.device.reportedName }}</dd>
        </div>
        <div v-if="props.device.source === 'managed'">
          <dt>Device ID</dt>
          <dd class="mono">{{ props.device.id }}</dd>
        </div>
      </dl>

      <section v-if="props.removable" class="panel manage-panel">
        <div class="manage-row">
          <div>
            <h2>{{ props.device.source === 'saved' ? 'Saved connection' : 'Remove device' }}</h2>
            <p v-if="props.device.source === 'saved'">
              Removing this entry only forgets it in this app. SSH access on the device stays in
              place.
            </p>
            <p v-else>
              Removing retires this device for your whole account: open sessions end and it can no
              longer connect until it is registered again.
            </p>
          </div>
          <button type="button" class="danger-quiet" :disabled="props.busy" @click="emit('remove')">
            <AppIcon name="trash" />Remove<span class="action-label">…</span>
          </button>
        </div>
      </section>
    </template>
    <PrivilegePanel
      v-if="
        props.tab === 'overview' &&
        props.device.privileged?.available &&
        !props.device.personalAccess
      "
      :key="props.device.id"
      :device="props.device"
      :adapter="props.adapter"
    />
    <FilesPanel
      v-else-if="props.tab === 'files'"
      :key="props.device.id"
      :device="props.device"
      :adapter="props.adapter"
    />
    <ServerPanel
      v-else-if="
        !props.device.personalAccess &&
        isServer &&
        (props.tab === 'logs' || props.tab === 'resources')
      "
      :key="`${props.device.id}-${props.tab}`"
      :device="props.device"
      :adapter="props.adapter"
      :tab="props.tab"
      :initial-unit="journalUnit"
      @terminal="emit('open', 'terminal')"
      @logs="
        (unit) => {
          journalUnit = unit;
          emit('navigate', 'logs');
        }
      "
    />
    <ToolPreview
      v-else-if="
        !props.device.personalAccess && (props.tab === 'logs' || props.tab === 'resources')
      "
      :tool="props.tab"
      :device="props.device"
    />
  </section>
</template>
